import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { SyncState } from '../sync/useSync.js';

/**
 * Zeigt, ob etwas noch nicht beim Server ist.
 *
 * Das ist keine Zierde. Eine Offline-App, die nicht sagt, was noch aussteht,
 * unterscheidet sich für den Nutzer nicht von einer, die Daten verliert — und
 * genau dieses Misstrauen bringt Leute dazu, alles zweimal einzutragen.
 *
 * In der Anzeigetafel ist das zweigeteilt: **immer** ein Chip in der Kopfzeile,
 * und **nur wenn etwas aussteht** eine Kachel ganz oben im Inhalt. Ein Zustand,
 * der nichts von einem verlangt, braucht keinen Platz; einer, der es verlangt,
 * gehört an die Stelle, auf die man ohnehin zuerst schaut.
 */

interface Reading {
  label: string;
  /**
   * Kurzform für den Chip. Die Kopfzeile teilt sich 375 px mit Name und
   * Einstellungen — ein ganzer Satz schiebt die App seitlich aus dem Bild.
   */
  short?: string;
  tone: 'good' | 'warn' | 'danger';
  needsAttention: boolean;
}

/** Wie der Abgleich gerade steht — auch für das Ziehen zum Aktualisieren. */
export function readSync(
  state: Pick<SyncState, 'isOnline' | 'failed' | 'held' | 'lastResult' | 'isSyncing' | 'pending'>,
  t: TFunction,
): Reading {
  return read(state, t);
}

function read(
  state: Pick<SyncState, 'isOnline' | 'failed' | 'held' | 'lastResult' | 'isSyncing' | 'pending'>,
  t: TFunction,
): Reading {
  if (!state.isOnline) {
    return {
      label: t('sync.offline', { count: state.pending }),
      tone: 'warn',
      needsAttention: true,
    };
  }
  if (state.failed > 0) {
    return {
      label: t('sync.failed', { count: state.failed }),
      tone: 'danger',
      needsAttention: true,
    };
  }
  if (state.held > 0) {
    // Heilt nicht von allein: Erst eine neue Fassung des Eintrags löst es.
    return {
      label: t('sync.heldEmpty', { count: state.held }),
      short: t('sync.heldEmptyShort'),
      tone: 'danger',
      needsAttention: true,
    };
  }
  if (state.lastResult?.error) {
    // Unerreichbar heilt sich von selbst, sobald der Server wieder da ist —
    // das soll man lesen können, statt „fehlgeschlagen" zu raten.
    if (state.lastResult.errorKind === 'unreachable') {
      return {
        label: t('sync.unreachable', { count: state.pending }),
        short: t('sync.unreachableShort'),
        tone: 'warn',
        needsAttention: true,
      };
    }
    if (state.lastResult.errorKind === 'update') {
      return {
        label: t('sync.updateRequired', { count: state.pending }),
        short: t('sync.updateRequiredShort'),
        tone: 'warn',
        needsAttention: true,
      };
    }
    if (state.lastResult.errorKind === 'server' && state.lastResult.status) {
      return {
        label: t('sync.serverError', { status: state.lastResult.status }),
        short: t('sync.serverErrorShort', { status: state.lastResult.status }),
        tone: 'danger',
        needsAttention: true,
      };
    }
    return { label: t('sync.error'), tone: 'danger', needsAttention: true };
  }
  if (state.isSyncing) {
    return { label: t('sync.syncing'), tone: 'warn', needsAttention: false };
  }
  if (state.pending > 0) {
    // Sichtbar im Chip, aber **ohne** Kachel: Online heilt das der Motor in
    // Sekunden von allein. Eine Kachel, die bei jedem eingetragenen Satz
    // aufspringt und den Bildschirm nach unten schiebt, kostet mitten in der
    // Einheit mehr, als sie sagt. Offline (oben) und abgelehnt (unten) bleiben
    // Kachel-würdig — die heilen nicht von allein.
    return {
      label: t('sync.pending', { count: state.pending }),
      tone: 'warn',
      needsAttention: false,
    };
  }
  return { label: t('sync.done'), tone: 'good', needsAttention: false };
}

const DOT: Record<Reading['tone'], string> = {
  good: 'bg-good',
  warn: 'bg-warn',
  danger: 'bg-danger',
};

const TEXT: Record<Reading['tone'], string> = {
  good: 'text-ink-muted',
  warn: 'text-warn',
  danger: 'text-danger',
};

/** Der Chip in der Kopfzeile — sagt den Zustand, verlangt nichts. */
export function SyncBadge({ state }: { state: SyncState }): React.JSX.Element {
  const { t } = useTranslation();
  const reading = read(state, t);

  return (
    // `min-w-0` + `truncate`: Wird es trotz Kurzform eng (große Schrift,
    // Englisch), kürzt der Chip — die Seite scrollt nie seitlich.
    <span className="kw-chip min-w-0">
      <span className={`size-2 shrink-0 rounded-full ${DOT[reading.tone]}`} aria-hidden="true" />
      {/* Vorgelesen wird der ganze Satz — nur das Auge bekommt die Kurzform. */}
      <span role="status" aria-live="polite" className="sr-only">
        {reading.label}
      </span>
      <span aria-hidden="true" className={`truncate ${TEXT[reading.tone]}`}>
        {reading.short ?? reading.label}
      </span>
    </span>
  );
}

/**
 * Die Kachel für alles, was noch aussteht.
 *
 * Abgelehntes bekommt **Grund und Ausweg**: Ohne einen Knopf zum Wiederholen
 * wäre „abgelehnt" eine Sackgasse, aus der auch ein reparierter Server nicht
 * mehr herausführt.
 */
export function SyncProblem({ state }: { state: SyncState }): React.JSX.Element | null {
  const { t } = useTranslation();
  const reading = read(state, t);

  if (!reading.needsAttention) return null;

  return (
    <div className="kw-tile border-warn">
      <p className={`kw-kicker ${TEXT[reading.tone]}`}>{reading.label}</p>

      {state.held > 0 ? (
        <p className="mt-1 text-sm text-ink-muted">{t('sync.heldEmptyHint')}</p>
      ) : null}

      {state.failedReason ? (
        <p className="mt-1 text-sm text-ink-muted">
          {t('sync.reason', { reason: state.failedReason })}
        </p>
      ) : null}

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={state.syncNow}
          disabled={state.isSyncing}
          className="kw-btn kw-btn-small flex-1"
        >
          {t('sync.now')}
        </button>
        {state.failed > 0 ? (
          <button
            type="button"
            onClick={state.retryNow}
            disabled={state.isSyncing}
            className="kw-btn kw-btn-quiet kw-btn-small flex-1 border-danger text-danger"
          >
            {t('sync.retry')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
